import React, {useId} from 'react';
import './HourglassIcon.css';

// Силуэт снят с /img/icons/hourglass.png (той самой иконки ожидания на
// CompletePhaseButton) по сетке 54x54, поэтому вместо нее компонент встает
// один в один: те же пропорции колб, та же толщина каркаса и шляпок.
const VIEW_BOX_SIZE = 54;

const FRAME_LEFT_PATH = 'M11.6 6.5C11.6 16 14.2 20.5 21.6 27C14.2 33.5 11.6 38 11.6 47.5';
const FRAME_RIGHT_PATH = 'M42.4 6.5C42.4 16 39.8 20.5 32.4 27C39.8 33.5 42.4 38 42.4 47.5';

// Стекло — по нему обрезается песок. Верхняя колба; нижняя получается тем же
// контуром, отраженным относительно середины часов.
//
// Границы идут не по внутренней кромке стенок, а по их осевым линиям (те же
// кривые, что у каркаса выше): песок заходит на стенку внахлест до ее середины,
// и стыка между ними не видно. Наружу он при этом не вылезет — до внешней
// кромки остается половина толщины стенки. Сверху контур уходит под шляпку по
// той же причине
const GLASS_PATH = 'M11.6 6.5C11.6 16 14.2 20.5 21.6 27L32.4 27C39.8 20.5 42.4 16 42.4 6.5Z';

const MIRROR_TRANSFORM = `matrix(1 0 0 -1 0 ${VIEW_BOX_SIZE})`;

// Днища растут внутрь от прежних краев, поэтому габариты иконки не меняются
const CAP_HEIGHT = 7.5;
const CAP_TOP_Y = 1.5;
const CAP_BOTTOM_Y = VIEW_BOX_SIZE - CAP_TOP_Y - CAP_HEIGHT;

// Песок — не фигура по размеру колбы, а «все, что ниже кромки»: широкое тело,
// уходящее вниз за пределы иконки. Форму ему на каждом уровне придает обрезка по
// стеклу, а анимация только опускает и поднимает кромку. Поэтому песок не
// сплющивается к концу пересыпания и не может вылезти за сужающиеся стенки —
// чем ниже кромка, тем уже он сам. И плоская сторона у него ровная: снизу его
// подрезает донышко часов.
//
// Кромки у двух фигур разные: у сыплющегося песка воронка (как на исходной
// png), у растущей горки — купол
const SAND_DRAIN_PATH = 'M2 4.75L16 4.75Q27 9.25 38 4.75L52 4.75L52 60L2 60Z';
const SAND_PILE_PATH = 'M2 6.5L16 6.5Q27 -1.5 38 6.5L52 6.5L52 60L2 60Z';

/**
 * Анимированные песочные часы: песок пересыпается вниз, затем часы
 * переворачиваются, и все начинается заново (цикл 10 с).
 *
 * Цвет берется из currentColor — иконка красится тем же цветом, что и текст
 * вокруг, размер задается пропом size.
 */
function HourglassIcon({size = 24, className = '', label = ''}) {
  // Обрезка адресуется по id, а часов на экране может быть несколько. Двоеточия
  // из useId убираем: в ссылке url(#...) они допустимы, но такой id — невалидный
  // CSS-идентификатор, и первый же селектор по нему споткнется
  const idBase = useId().replace(/:/g, '');
  const glassTopId = `${idBase}-glass-top`;
  const glassBottomId = `${idBase}-glass-bottom`;

  // Половина цикла: песок в верхней колбе опускается, в нижней растет. Вторая
  // половина — то же самое, но часы уже перевернуты, поэтому колбы поменялись
  // ролями: там работает второй такой же комплект, отраженный по вертикали.
  // Комплекты сменяют друг друга в тот момент, когда показывают одно и то же,
  // так что подмена не видна
  const sand = (
    <>
      <g clipPath={`url(#${glassTopId})`}>
        <path className="hourglass-icon__sand hourglass-icon__sand_drain" d={SAND_DRAIN_PATH}/>
      </g>
      <g clipPath={`url(#${glassBottomId})`}>
        <path className="hourglass-icon__sand hourglass-icon__sand_pile" d={SAND_PILE_PATH}/>
      </g>
    </>
  );

  return (
    <svg
      className={`hourglass-icon ${className}`.trim()}
      viewBox={`0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`}
      width={size}
      height={size}
      // Без подписи часы — украшение при соседнем тексте; с подписью читалка
      // экрана произносит ее вместо alt заменённой картинки
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : 'true'}
      focusable="false"
    >
      {label && <title>{label}</title>}
      <defs>
        <clipPath id={glassTopId}>
          <path d={GLASS_PATH}/>
        </clipPath>
        <clipPath id={glassBottomId}>
          <path d={GLASS_PATH} transform={MIRROR_TRANSFORM}/>
        </clipPath>
      </defs>
      <g className="hourglass-icon__body">
        <rect className="hourglass-icon__cap" x="5" y={CAP_TOP_Y} width="44" height={CAP_HEIGHT} rx="2"/>
        <rect className="hourglass-icon__cap" x="5" y={CAP_BOTTOM_Y} width="44" height={CAP_HEIGHT} rx="2"/>
        <path className="hourglass-icon__frame" d={FRAME_LEFT_PATH}/>
        <path className="hourglass-icon__frame" d={FRAME_RIGHT_PATH}/>

        <g className="hourglass-icon__fill hourglass-icon__fill_first">{sand}</g>
        <g className="hourglass-icon__fill hourglass-icon__fill_second" transform={MIRROR_TRANSFORM}>
          {sand}
        </g>
      </g>
    </svg>
  );
}

export default HourglassIcon;
